import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from '@/components/ui/sonner';
import { organizationService } from "@/services/core/organization.service";
import { 
  CreateOrganizationRequest, 
  UpdateOrganizationRequest,
  AddOrganizationMemberRequest,
  UpdateOrganizationMemberRequest
} from "@/types/organization";

// Query keys
export const organizationKeys = {
  all: ["organizations"] as const,
  lists: () => [...organizationKeys.all, "list"] as const,
  list: (filters?: Record<string, unknown>) => [...organizationKeys.lists(), filters] as const,
  details: () => [...organizationKeys.all, "detail"] as const,
  detail: (id: string) => [...organizationKeys.details(), id] as const,
  members: (id: string) => [...organizationKeys.detail(id), "members"] as const,
  userRole: (id: string) => [...organizationKeys.detail(id), "userRole"] as const,
};

// Get all organizations
export const useOrganizations = (filters?: Record<string, unknown>) => {
  return useQuery({
    queryKey: organizationKeys.list(filters),
    queryFn: async () => {
      const response = await organizationService.getUserOrganizations();
      if (!response.success) {
        throw new Error(response.error || "Failed to fetch organizations");
      }
      return response.data || [];
    },
  });
};

// Get organization by ID
export const useOrganization = (id: string) => {
  return useQuery({
    queryKey: organizationKeys.detail(id),
    queryFn: async () => {
      const response = await organizationService.getOrganizationById(id);
      if (!response.success) {
        throw new Error(response.error || "Failed to fetch organization");
      }
      return response.data;
    },
    enabled: !!id,
  });
};

// Create organization
export const useCreateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateOrganizationRequest) => {
      const response = await organizationService.createOrganization(data);
      if (!response.success) {
        throw new Error(response.error || "Failed to create organization");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      toast.success("Success", { description: "Organization created successfully" });
    },
    onError: (error: Error) => {
      toast.error("Error", { description: error.message });
    },
  });
};

// Update organization
export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateOrganizationRequest }) => {
      const response = await organizationService.updateOrganization(id, data);
      if (!response.success) {
        throw new Error(response.error || "Failed to update organization");
      }
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
    },
    onError: (error: Error) => {
      toast.error("Error", { description: error.message });
    },
  });
};

// Delete organization
export const useDeleteOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await organizationService.deleteOrganization(id);
      if (!response.success) {
        throw new Error(response.error || "Failed to delete organization");
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
      toast.success("Success", { description: "Organization deleted successfully" });
    },
    onError: (error: Error) => {
      toast.error("Error", { description: error.message });
    },
  });
};

// Get organization members
export const useOrganizationMembers = (organizationId: string) => {
  return useQuery({
    queryKey: organizationKeys.members(organizationId),
    queryFn: async () => {
      const response = await organizationService.getOrganizationMembers(organizationId);
      if (!response.success) {
        throw new Error(response.error || "Failed to fetch organization members");
      }
      return response.data || [];
    },
    enabled: !!organizationId,
  });
};

// Add organization member
export const useAddOrganizationMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: AddOrganizationMemberRequest }) => {
      const response = await organizationService.addMember(organizationId, data);
      if (!response.success) {
        throw new Error(response.error || "Failed to add member");
      }
      return response.data;
    },
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(organizationId) });
      toast.success("Success", { description: "Member added successfully" });
    },
    onError: (error: Error) => {
      toast.error("Error", { description: error.message });
    },
  });
};

// Update organization member role
export const useUpdateOrganizationMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      organizationId, 
      userId, 
      data 
    }: { 
      organizationId: string; 
      userId: string; 
      data: UpdateOrganizationMemberRequest 
    }) => {
      const response = await organizationService.updateMemberRole(organizationId, userId, data);
      if (!response.success) {
        throw new Error(response.error || "Failed to update member role");
      }
      return response.data;
    },
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(organizationId) });
      toast.success("Success", { description: "Member role updated successfully" });
    },
    onError: (error: Error) => {
      toast.error("Error", { description: error.message });
    },
  });
};

// Remove organization member
export const useRemoveOrganizationMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, userId }: { organizationId: string; userId: string }) => {
      const response = await organizationService.removeMember(organizationId, userId);
      if (!response.success) {
        throw new Error(response.error || "Failed to remove member");
      }
      return response.data;
    },
    onSuccess: (_, { organizationId }) => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.members(organizationId) });
      toast.success("Success", { description: "Member removed successfully" });
    },
    onError: (error: Error) => {
      toast.error("Error", { description: error.message });
    },
  });
};

// Get user's role in organization
export const useOrganizationUserRole = (organizationId: string) => {
  return useQuery({
    queryKey: organizationKeys.userRole(organizationId),
    queryFn: async () => {
      const response = await organizationService.getUserRole(organizationId);
      if (!response.success) {
        throw new Error(response.error || "Failed to fetch user role");
      }
      return response.data;
    },
    enabled: !!organizationId,
  });
};
